import { useEffect, useState } from 'react';
import { useRegisterMutation } from '../../app/services/manuals';
import { useTranslation } from 'react-i18next';
import { commonTextFieldStyles } from '../../utils/textFieldStyles';
import DragDropFileUpload from '../DragDropFileUpload';
import ModalComponent from '../ModalComponent';
import ConfirmDiscardDialog from '../ConfirmDiscardDialog';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';

import {
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { ModalButton, ModalDialogActions } from '../ModalComponents';

import styles from '../ModalComponent/modalStyles.module.css';
import { validateFormData, getValidationErrorMessage } from '../../helpers';
import { postManualSchema } from '../../schemas';
import { INPUT_FILE_ACCEPT_DOC } from '../../constants';

const initialForm = {
  name: '',
  description: '',
};

export default function ModalAddManual({ id, fetchData }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errorResponse, setErrorResponse] = useState('');
  const [registerManual, { error }] = useRegisterMutation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const { t } = useTranslation();

  const [formValidationErrors, setFormValidationErrors] = useState(null);
  const nameError = getValidationErrorMessage(formValidationErrors, 'name');
  const descrError = getValidationErrorMessage(formValidationErrors, 'description');
  const fileError = getValidationErrorMessage(formValidationErrors, 'file');

  const { guardClose, confirmProps } = useUnsavedChangesWarning(isDirty, open);

  const handleOpen = () => setOpen(true);
  const doClose = () => {
    setOpen(false);
    setForm(initialForm);
    setSelectedFile(null);
    setFormValidationErrors(null);
    setIsDirty(false);
  };
  const handleClose = () => guardClose(doClose);

  useEffect(() => {
    if (error) {
      console.log(error);
      setErrorResponse(error.data?.message);
    }
  }, [error]);

  const handleChange = (e) => {
    setErrorResponse('');
    setIsDirty(true);
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    const validationErrors = validateFormData(t, postManualSchema, { ...form, file: selectedFile });
    if (!!validationErrors) {
      setFormValidationErrors(validationErrors);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('description', form.description);
      formData.append('machineGroup', JSON.stringify({ id }));
      formData.append('file', selectedFile);
      await registerManual(formData).unwrap();
      await fetchData();
      doClose();
    } catch (err) {
      const message =
        err?.data?.message || err?.message || 'Unexpected error';
      setErrorResponse(message);
    }
  };

  return (
    <div>
      <ModalButton
        variant="contained"
        color="primary"
        onClick={handleOpen}
        style={{ padding: '12px 24px', textTransform: 'none' }}
        startIcon={<AddIcon />}
      >
        {t('modals.add')}
      </ModalButton>
      <ModalComponent open={open} handleClose={handleClose}>
        <div className={styles.modal}>
          <DialogTitle sx={{ m: 0, p: 2 }}>{t('modals.addManual')}</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers sx={{ p: 3 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <TextField
                label={t('generic.name')}
                variant="outlined"
                name="name"
                onChange={handleChange}
                fullWidth
                error={!!nameError}
                helperText={nameError ?? ' '}
                sx={commonTextFieldStyles}
              />
              <TextField
                label={t('generic.description')}
                variant="outlined"
                name="description"
                onChange={handleChange}
                fullWidth
                error={!!descrError}
                helperText={descrError ?? ' '}
                sx={commonTextFieldStyles}
              />

              <DragDropFileUpload
                accept={INPUT_FILE_ACCEPT_DOC}
                selectedFile={selectedFile}
                onFileChange={(file, error) => {
                  setIsDirty(true);
                  if (error) {
                    setErrorResponse(error);
                    setSelectedFile(null);
                  } else {
                    setErrorResponse('');
                    setSelectedFile(file);
                  }
                }}
                onFileRemove={() => {
                  setSelectedFile(null);
                  setErrorResponse('');
                }}
                error={fileError}
                helperText={t('generic.fileUploadMachineGroup')}
                maxSize={process.env.REACT_APP_MAX_FILE_SIZE || 10485760}
                placeholder={t('modals.uploadDocument')}
              />

              {errorResponse &&
                (Array.isArray(errorResponse) ? (
                  errorResponse.map((item, index) => (
                    <Typography key={index} color="error">
                      {item}
                    </Typography>
                  ))
                ) : (
                  <Typography color="error">{errorResponse}</Typography>
                ))}
            </div>
          </DialogContent>
          <ModalDialogActions>
            <ModalButton
              variant="outlined"
              color="primary"
              onClick={handleClose}
            >
              {t('modals.cancel')}
            </ModalButton>
            <ModalButton
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              {t('modals.add')}
            </ModalButton>
          </ModalDialogActions>
        </div>
      </ModalComponent>
      <ConfirmDiscardDialog {...confirmProps} />
    </div>
  );
}
